import { useEffect } from 'react';

import { ProfileModel, exportingApi, ordersApi, profileApi, reportingApi, syncBillApi } from '@pukka/api';
import { clickstreamTracker } from '@pukka/clickstream';
import { hookUtils, dateUtils, assertUtil, animationUtil } from '@pukka/common/utils';
import { Channel, pusher } from '@pukka/pusher';
import { toast } from '@pukka/toast';
import { proxy, proxySet, subscribeKey, useSnapshot, withPersist } from '@pukka/valtio';
import { Mutex } from 'async-mutex';
import debounce from 'lodash/debounce';

import { getNowInsteadNull, useInitialData } from './utils';

type TAB = 'success' | 'cancelled' | 'void';
type STATUS = 'success' | 'loading' | 'error';
type EXPORT_CHANNEL = 'order-export-transaction' | 'order-export-item-detail';
type Job = {
  mutex: Mutex;
  channel: Channel;
  status?: STATUS;
};

const exportsMap = new Map<EXPORT_CHANNEL, Job>();

interface ITransactionState {
  outlets: {
    key: number;
    value: string;
    meta: ProfileModel.Outlet;
  }[];
  outletById: Map<number, ProfileModel.Outlet>;
  businessId: null | number;
  success: {
    count: number;
    total: number;
    net: number;
  } | null;
  cancelled: {
    count: number;
    total: number;
  } | null;
  void: {
    count: number;
    total: number;
  } | null;
  readonly size: number;
}

interface ITransactionPersistedState extends Record<string, unknown> {
  selectedOutletId: number;
  startDate: Date;
  endDate: Date;
  receipt: string;
  orderId: string;
  itemName: string;
  allDay: boolean;
}

interface ITransactionSuccessState {
  status: STATUS;
  data: {
    token: string;
    group: string;
    outletName: string;
    date: Date;
    by: string;
    itemName: string;
    total: number;
  }[];
  error: string;
  isAllFetched: boolean;
  cursor?: string;
  exports: Set<EXPORT_CHANNEL>;
}

interface ITransactionCancelledState {
  status: STATUS;
  data: {
    token: string;
    group: string;
    outletId: number;
    outletName: string;
    date: Date;
    by: string;
    reason: string;
    total: number;
  }[];
  error: string;
  isAllFetched: boolean;
  isExporting: boolean;
  cursor?: string;
}

interface ITransactionVoidState {
  status: STATUS;
  data: {
    token: string;
    group: string;
    date: string;
    orderId: string;
    itemName: string;
    quantity: string;
    reason: string;
    by: string;
    total: number;
  }[];
  error: string;
  isAllFetched: boolean;
  isExporting: boolean;
  cursor?: string;
}

const TransactionState = proxy<ITransactionState>({
  outlets: [],
  outletById: new Map(),
  businessId: null,
  success: null,
  cancelled: null,
  void: null,
  get size(): number {
    return TransactionState.outlets.length;
  },
});

const SuccessTransactionState = proxy<ITransactionSuccessState>({
  status: 'success',
  error: '',
  data: [],
  isAllFetched: false,
  cursor: undefined,
  exports: proxySet(),
});

const CancelledTransactionState = proxy<ITransactionCancelledState>({
  status: 'success',
  error: '',
  data: [],
  isAllFetched: false,
  isExporting: false,
  cursor: undefined,
});

const VoidTransactionState = proxy<ITransactionVoidState>({
  status: 'success',
  error: '',
  data: [],
  isAllFetched: false,
  isExporting: false,
  cursor: undefined,
});

const { proxy: TransactionPersistedState } = withPersist<ITransactionPersistedState>(
  {
    selectedOutletId: -1,
    startDate: new Date(NaN),
    endDate: new Date(NaN),
    receipt: '',
    itemName: '',
    orderId: '',
    allDay: true,
  },
  'report-transaction',
  {
    expiry: 1000 * 60 * 60 * 24, // 1 day expiry
    session: 'user', // removed on logout
  },
);

async function fetchSuccessTransactionList(
  State = SuccessTransactionState,
  PersistedState = TransactionPersistedState,
  getter = ordersApi.getOrders,
) {
  if (State.isAllFetched || State.status === 'loading') {
    return false;
  }

  State.status = 'loading';

  try {
    const { selectedOutletId, startDate, endDate, receipt } = PersistedState;

    const data = await getter({
      outletIds: selectedOutletId === -1 ? TransactionState.outlets.map((o) => o.key).filter((k) => k !== -1) : [selectedOutletId],
      startTime: getNowInsteadNull(startDate, 'start', "yyyy-MM-dd'T'HH:mm:ss"),
      endTime: getNowInsteadNull(endDate, 'end', "yyyy-MM-dd'T'HH:mm:ss"),
      keyword: receipt,
      nextCursor: State.cursor,
    });

    // Data is still valid
    if (
      selectedOutletId === PersistedState.selectedOutletId &&
      startDate === PersistedState.startDate &&
      endDate === PersistedState.endDate &&
      receipt === PersistedState.receipt
    ) {
      State.data = (State.cursor ? State.data : []).concat(
        data.orders.map((o) => ({
          token: o.order_token,
          outletName: TransactionState.outletById.get(o.outlet_id)?.name ?? '',
          by: o.created_by,
          date: new Date(o.created_at),
          itemName: o.item_name,
          total: o.total_collected,
          group: dateUtils.formatDate(o.created_at, 'EEEE, MMMM do, yyyy'),
        })),
      );

      State.cursor = data.next_cursor;
      State.status = 'success';
      State.isAllFetched = data.next_cursor === null;
    }
  } catch (error: unknown) {
    assertUtil.assertObjectIsTruthy<{
      message: string;
    }>(error);

    toast.error(error.message);
    State.status = 'error';
  }

  return true;
}

async function fetchCancelledTransactionList(
  State = CancelledTransactionState,
  PersistedState = TransactionPersistedState,
  getter = syncBillApi.getBills,
) {
  if (State.isAllFetched || State.status === 'loading') {
    return false;
  }

  State.status = 'loading';

  try {
    const cursor = State.cursor ? Number(State.cursor) : 1;
    const { selectedOutletId, startDate, endDate, orderId } = PersistedState;

    const data = await getter(
      selectedOutletId === -1 ? TransactionState.outlets.map((o) => o.key).filter((k) => k !== -1) : selectedOutletId,
      {
        orderId,
        startDate: getNowInsteadNull(startDate, 'start'),
        endDate: getNowInsteadNull(endDate, 'end'),
        statuses: 'CANCELLED',
        page: cursor,
      },
    );

    if (
      selectedOutletId === PersistedState.selectedOutletId &&
      startDate === PersistedState.startDate &&
      endDate === PersistedState.endDate &&
      orderId === PersistedState.orderId
    ) {
      State.data = (cursor > 1 ? State.data : []).concat(
        data.map((o) => ({
          token: String(o.id),
          outletId: o.outletId,
          outletName: TransactionState.outletById.get(o.outletId)?.name ?? '',
          date: new Date(o.cancelledAt ?? new Date()),
          by: o.cancelledByName ?? '',
          reason: o.cancelledReason ?? '',
          total: o.totalPrice ?? 0,
          group: dateUtils.formatDate(o.cancelledAt, 'EEEE, MMMM do, yyyy'),
        })),
      );

      State.cursor = String(cursor + 1);
      State.status = 'success';
      State.isAllFetched = data.length < 50;
    }
  } catch (error: unknown) {
    assertUtil.assertObjectIsTruthy<{
      message: string;
    }>(error);

    toast.error(error.message);
    State.status = 'error';
  }

  return true;
}

async function fetchVoidTransactionList(
  State = VoidTransactionState,
  PersistedState = TransactionPersistedState,
  getter = syncBillApi.getVoidItems,
) {
  if (State.isAllFetched || State.status === 'loading') {
    return false;
  }

  State.status = 'loading';

  try {
    const cursor = State.cursor ? Number(State.cursor) : 1;
    const { selectedOutletId, startDate, endDate, itemName } = PersistedState;

    const data = await getter(
      selectedOutletId === -1 ? TransactionState.outlets.map((o) => o.key).filter((k) => k !== -1) : selectedOutletId,
      {
        itemName,
        startDate: getNowInsteadNull(startDate, 'start'),
        endDate: getNowInsteadNull(endDate, 'end'),
        page: cursor,
        statuses: 'COMPLETE,CANCELLED',
      },
    );

    if (
      selectedOutletId === PersistedState.selectedOutletId &&
      startDate === PersistedState.startDate &&
      endDate === PersistedState.endDate &&
      itemName === PersistedState.itemName
    ) {
      State.data = (cursor > 1 ? State.data : []).concat(
        data.map((o) => ({
          orderId: o.orderId,
          date: dateUtils.formatDate(o.cancelledTime, 'yyyy-MM-dd HH:mm:ss'),
          by: o.cancelledByName ?? '',
          itemName: o.itemName,
          quantity: String(o.itemCount),
          reason: o.cancelledReason,
          total: o.itemTotalPrice,
          token: o.orderId,
          group: dateUtils.formatDate(o.cancelledTime, 'EEEE, MMMM do, yyyy'),
        })),
      );

      State.cursor = String(cursor + 1);
      State.status = 'success';
      State.isAllFetched = data.length < 50;
    }
  } catch (error: unknown) {
    assertUtil.assertObjectIsTruthy<{
      message: string;
    }>(error);

    toast.error(error.message);
    State.status = 'error';
  }

  return true;
}

async function onFetchNextData(tab: TAB) {
  switch (tab) {
    case 'cancelled':
      await fetchCancelledTransactionList();
      break;
    case 'void':
      await fetchVoidTransactionList();
      break;
    case 'success':
    default:
      await fetchSuccessTransactionList();
      break;
  }

  return true;
}

function onResetData(
  tab: TAB,
  States = [SuccessTransactionState, CancelledTransactionState, VoidTransactionState],
  onFetch = onFetchNextData,
) {
  States.forEach((state) => {
    state.data = [];
    state.error = '';
    state.cursor = undefined;
    state.status = 'success';
    state.isAllFetched = false;
  });

  onFetch(tab);
}

function onOutletChange(
  tab: TAB,
  onCloseDetail: () => void,
  outletId: number,
  PersistedState = TransactionPersistedState,
  onReset = onResetData,
) {
  PersistedState.selectedOutletId = outletId;

  onCloseDetail();
  onReset(tab);
}

function onSetDate(
  tab: TAB,
  onCloseDetail: () => void,
  start?: Date,
  end?: Date,
  allDay?: boolean,
  PersistedState = TransactionPersistedState,
  onReset = onResetData,
) {
  if (start) {
    PersistedState.startDate = start;
  }

  if (end) {
    PersistedState.endDate = end;
  }

  PersistedState.allDay = !!allDay;

  onCloseDetail();
  onReset(tab);
}

const onChangeSearch = debounce(
  (tab: TAB, onCloseDetail: () => void, keyword: string, PersistedState = TransactionPersistedState, onReset = onResetData) => {
    animationUtil.doTransition(() => {
      switch (tab) {
        case 'cancelled':
          PersistedState.orderId = keyword;
          break;
        case 'void':
          PersistedState.itemName = keyword;
          break;
        case 'success':
        default:
          PersistedState.receipt = keyword;
          break;
      }

      onCloseDetail();
      onReset(tab);
    });
  },
  500,
);

function subscriptionStartFactory(
  channel: EXPORT_CHANNEL,
  job: Job,
  PersistedState = TransactionPersistedState,
  exportFileInit = exportingApi.exportFile,
) {
  return async () => {
    const release = await job.mutex.acquire();

    const outletId = PersistedState.selectedOutletId === -1 ? null : PersistedState.selectedOutletId;

    try {
      await exportFileInit(
        {
          feature_name: channel,
          additional: {
            start_time: getNowInsteadNull(PersistedState.startDate, 'start', "yyyy-MM-dd'T'HH:mm:ss"),
            end_time: getNowInsteadNull(PersistedState.endDate, 'end', "yyyy-MM-dd'T'HH:mm:ss"),
            outlet_id: outletId,
          },
        },
        outletId,
      );

      release();
    } catch (error) {
      assertUtil.assertObjectIsTruthy<{
        message: string;
      }>(error);

      toast.error(error.message);
    }

    job.channel.unbind('pusher:subscription_succeeded');
  };
}

function subscriptionUpdateFactory(
  channel: EXPORT_CHANNEL,
  job: Job,
  SuccessState = SuccessTransactionState,
  exportFileGetter = exportingApi.getExportFile,
) {
  return async ({ id, status }: { id: string; status: 'IN_PROGRESS' | 'SUCCESS' | 'FAILURE' }) => {
    await job.mutex.waitForUnlock();

    if (status === 'IN_PROGRESS') {
      return;
    }

    try {
      const { file_url: fileUrl, status: _status, error_message: errorMessage } = await exportFileGetter(id);

      if (_status === 'FAILURE') {
        throw new Error(errorMessage);
      }

      window.open(fileUrl);
      job.status = 'success';
    } catch (error) {
      assertUtil.assertObjectIsTruthy<{
        message: string;
      }>(error);

      toast.error(error.message);
      job.status = 'error';
    }

    job.channel.unbind('export');
    job.channel.unsubscribe();

    // Remove job
    SuccessState.exports.delete(channel);
    exportsMap.delete(channel);
  };
}

async function onExportSuccessTransactions(
  channel: EXPORT_CHANNEL,
  State = TransactionState,
  SuccessState = SuccessTransactionState,
  PersistedState = TransactionPersistedState,
  exportFileInit = exportingApi.exportFile,
  exportFileGetter = exportingApi.getExportFile,
) {
  if (!State.businessId) {
    return false;
  }

  if (!SuccessState.exports.has(channel)) {
    SuccessState.exports.add(channel);
    exportsMap.set(channel, {
      mutex: new Mutex(),
      channel: pusher.subscribe(`export-${State.businessId}-${channel}`),
    });
  }

  const job = exportsMap.get(channel);

  if (!job || job.status === 'loading') {
    return false;
  }

  job.status = 'loading';
  job.channel.bind('pusher:subscription_succeeded', subscriptionStartFactory(channel, job, PersistedState, exportFileInit));
  job.channel.bind('export', subscriptionUpdateFactory(channel, job, SuccessState, exportFileGetter));

  return true;
}

async function onExportCancelledTransactions(
  State = TransactionState,
  CancelledState = CancelledTransactionState,
  PersistedState = TransactionPersistedState,
  exporter = syncBillApi.getBillExportFile,
) {
  if (CancelledState.isExporting) {
    return false;
  }

  if (PersistedState.selectedOutletId === -1) {
    return false;
  }

  CancelledState.isExporting = true;

  try {
    await exporter(PersistedState.selectedOutletId, {
      startDate: getNowInsteadNull(PersistedState.startDate, 'start'),
      endDate: getNowInsteadNull(PersistedState.endDate, 'end'),
      outletName: State.outletById.get(PersistedState.selectedOutletId)?.name ?? '',
      status: 'CANCELLED',
    });
  } catch (error) {
    assertUtil.assertObjectIsTruthy<{
      message: string;
    }>(error);

    toast.error(error.message);
  }

  CancelledState.isExporting = false;

  return true;
}

async function onExportVoidTransactions(
  State = TransactionState,
  VoidState = VoidTransactionState,
  PersistedState = TransactionPersistedState,
  exporter = syncBillApi.getVoidItemExportFile,
) {
  if (VoidState.isExporting) {
    return false;
  }

  if (PersistedState.selectedOutletId === -1) {
    return false;
  }

  VoidState.isExporting = true;

  try {
    await exporter(PersistedState.selectedOutletId, {
      startDate: getNowInsteadNull(PersistedState.startDate, 'start'),
      endDate: getNowInsteadNull(PersistedState.endDate, 'end'),
      outletName: State.outletById.get(PersistedState.selectedOutletId)?.name ?? '',
      statuses: 'COMPLETE,CANCELLED',
    });
  } catch (error) {
    assertUtil.assertObjectIsTruthy<{
      message: string;
    }>(error);

    toast.error(error.message);
  }

  VoidState.isExporting = false;

  return true;
}

function onExportTransactions(tab: TAB, channel?: EXPORT_CHANNEL) {
  switch (tab) {
    case 'cancelled':
      onExportCancelledTransactions();
      break;
    case 'void':
      onExportVoidTransactions();
      break;
    case 'success':
    default:
      if (channel) {
        onExportSuccessTransactions(channel);
      }

      break;
  }
}

function useTransactionData(State = TransactionState, getter = profileApi.useProfile) {
  const { status, data } = getter();

  useEffect(() => {
    if (status === 'success' && data) {
      State.businessId = data.business?.id ?? null;

      State.outlets = [
        {
          key: -1,
          value: 'All Outlet',
          meta: {},
        },
      ].concat(
        data.outlets?.map((outlet) => ({
          key: outlet.id ?? 0,
          value: outlet.name ?? '',
          meta: outlet,
        })) ?? [],
      );

      State.outletById = new Map(
        (data.outlets ?? []).map((o) => {
          return [o.id, o] as [number, ProfileModel.Outlet];
        }),
      );
    }
  }, [data, status, State]);

  return status;
}

function useSuccessTransactionData(PersistedState = TransactionPersistedState) {
  const { outlets } = useSnapshot(TransactionState);
  const { selectedOutletId, startDate, endDate, receipt } = useSnapshot(PersistedState);
  const $scroller = document.querySelector('main');

  hookUtils.useOnEndReached($scroller, onFetchNextData.bind({}, 'success'));

  const { data, status } = reportingApi.useTransactionSummaryReport(
    selectedOutletId === -1 ? TransactionState.outlets.map((o) => o.key).filter((k) => k !== -1) : [selectedOutletId],
    startDate,
    endDate,
    receipt,
    { enabled: outlets.length > 0 },
  );

  useInitialData(TransactionState, () => {
    if (SuccessTransactionState.data.length === 0) {
      onFetchNextData('success');
    }
  });

  useEffect(() => {
    if (status === 'success' && data) {
      TransactionState.success = {
        count: data.transactionSummaryReport.numberOfTransactions,
        net: data.transactionSummaryReport.netSales,
        total: data.transactionSummaryReport.totalCollected,
      };
    }
  }, [data, status]);

  useEffect(() => {
    // Reset the void data when unmounted
    return () => {
      TransactionState.success = null;
    };
  }, []);

  return status;
}

function useCancelledTransactionData(PersistedState = TransactionPersistedState) {
  const { outlets } = useSnapshot(TransactionState);
  const { selectedOutletId, startDate, endDate, orderId } = useSnapshot(PersistedState);
  const $scroller = document.querySelector('main');

  hookUtils.useOnEndReached($scroller, onFetchNextData.bind({}, 'cancelled'));

  const { data, status } = syncBillApi.useBillSummary(
    selectedOutletId === -1 ? outlets.map((o) => o.key).filter((k) => k !== -1) : selectedOutletId,
    {
      startDate: getNowInsteadNull(startDate, 'start'),
      endDate: getNowInsteadNull(endDate, 'end'),
      statuses: 'CANCELLED',
      orderId,
    },
    { enabled: outlets.length > 0 },
  );

  useInitialData(TransactionState, () => {
    if (CancelledTransactionState.data.length === 0) {
      onFetchNextData('cancelled');
    }
  });

  useEffect(() => {
    if (status === 'success' && data) {
      TransactionState.cancelled = {
        count: data.cancelledCount,
        total: data.cancelledTotalPrice,
      };
    }
  }, [data, status]);

  useEffect(() => {
    // Reset the void data when unmounted
    return () => {
      TransactionState.cancelled = null;
    };
  }, []);

  return status;
}

function useVoidTransactionData(PersistedState = TransactionPersistedState) {
  const { outlets } = useSnapshot(TransactionState);
  const { selectedOutletId, startDate, endDate, itemName } = useSnapshot(PersistedState);
  const $scroller = document.querySelector('main');

  hookUtils.useOnEndReached($scroller, onFetchNextData.bind({}, 'void'));

  const { data, status } = syncBillApi.useVoidItemSummary(
    selectedOutletId === -1 ? outlets.map((o) => o.key).filter((k) => k !== -1) : selectedOutletId,
    {
      startDate: getNowInsteadNull(startDate, 'start'),
      endDate: getNowInsteadNull(endDate, 'end'),
      itemName,
      statuses: 'COMPLETE,CANCELLED',
    },
    { enabled: outlets.length > 0 },
  );

  useInitialData(TransactionState, () => {
    if (VoidTransactionState.data.length === 0) {
      onFetchNextData('void');
    }
  });

  useEffect(() => {
    if (status === 'success' && data) {
      TransactionState.void = {
        count: data.totalVoidItems,
        total: data.totalVoidPrice,
      };
    }
  }, [data, status]);

  useEffect(() => {
    // Reset the void data when unmounted
    return () => {
      TransactionState.void = null;
    };
  }, []);

  return status;
}

const unsub = subscribeKey(TransactionState, 'outlets', () => {
  const { selectedOutletId, startDate, endDate } = TransactionPersistedState;

  clickstreamTracker.trackPage('MOKAPOS_REPORT_TRANSACTIONS_START', {
    filter: {
      outletIds: selectedOutletId === -1 ? TransactionState.outlets.map((o) => o.key).filter((k) => k !== -1) : [selectedOutletId],
      startDate: getNowInsteadNull(startDate, 'start', "yyyy-MM-dd'T'HH:mm:ss"),
      endDate: getNowInsteadNull(endDate, 'end', "yyyy-MM-dd'T'HH:mm:ss"),
      allDay: true,
    },
  });

  unsub();
});

export type {
  ITransactionState,
  ITransactionSuccessState,
  ITransactionCancelledState,
  ITransactionVoidState,
  ITransactionPersistedState,
  TAB,
  STATUS,
};
export {
  TransactionState,
  TransactionPersistedState,
  SuccessTransactionState,
  CancelledTransactionState,
  VoidTransactionState,
  onOutletChange,
  onSetDate,
  onChangeSearch,
  onExportTransactions,
  exportsMap,
  useTransactionData,
  useSuccessTransactionData,
  useCancelledTransactionData,
  useVoidTransactionData,
  onResetData as _onResetData,
  fetchSuccessTransactionList as _fetchSuccessTransactionList,
  fetchCancelledTransactionList as _fetchCancelledTransactionList,
  fetchVoidTransactionList as _fetchVoidTransactionList,
  subscriptionStartFactory as _subscriptionStartFactory,
  subscriptionUpdateFactory as _subscriptionUpdateFactory,
  onExportSuccessTransactions as _onExportSuccessTransactions,
  onExportCancelledTransactions as _onExportCancelledTransactions,
  onExportVoidTransactions as _onExportVoidTransactions,
};
